import { Component, OnInit, OnDestroy, ViewChild, ElementRef, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PortalSettingsService } from '@core/services/portal-settings.service';
import { AccountService } from '@pages/account/services/account.service';
import { NotificationsService } from '@core/services/notifications.service';
import { UtilityService } from '@core/services/utility.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoginOnboardCoPickModalComponent } from '@modals/account/login-onboard-co-pick-modal/login-onboard-co-pick-modal.component';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { LoginFormService } from '@pages/account/services/login-form.service';
import { LoginCompanyInfo } from '@shared/model/login-company-info';

enum MainMenuType {
  Default = 'default',
  Enroll = 'enroll',
  SignInHelp = 'signInHelp',
  Link = 'link',
}
enum SubMenuType {
  Default = 'default',
  EmployeeLogin = 'employeeLogin',
  WindowsLogin = 'windowsLogin',
}

enum LoginType {
  Default = 'default',
  ClockInOut = 'clockInOut',
  FieldMode = 'fieldMode',
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit, OnDestroy {
  // Injected services
  private readonly portalSettings = inject(PortalSettingsService).getPortalSettings();
  private readonly accountService = inject(AccountService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly utilityService = inject(UtilityService);
  private readonly ngbModalService = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly loginFormService = inject(LoginFormService);
  private readonly document = inject(DOCUMENT);
  private readonly window = this.document.defaultView as Window;

  // Private fields (not used in template)
  private readonly enableViewpointOneLogin = this.portalSettings.ViewpointOneLoginEnabled.ConfigValue === '1';
  private readonly hideEnrollHelp = this.portalSettings.DisableEnrollPasswordChange.ConfigValue === '1';
  private companiesEnabledForOnboarding: any = [];
  private companyFromUrl: number = null;
  private loginErrorMsg =
    'Login failed - please confirm your information and retry.  Note that repeated login attempts may lock your account.';

  // Public fields (used in template)
  readonly isEMS_Active = this.portalSettings.EMS_Active.ConfigValue === '1';
  readonly welcomeText = this.portalSettings.LoginWelcomeRichText.ConfigValue;
  readonly enableEnrollAndPasswordReset = this.portalSettings.DisableEnrollPasswordChange.ConfigValue === '0';
  readonly enableClockInOut = this.portalSettings.ClockInLoginEnabled.ConfigValue === '1';
  readonly enableFieldMode = this.portalSettings.FieldLoginEnabled.ConfigValue === '1';
  readonly isVistaTidEnabled = this.portalSettings.TidEnabled.ConfigValue === '1';
  readonly passwordResetButtonText = this.portalSettings.ResetPasswordOverrideText.ConfigValue ?? 'Need sign in help?';
  readonly signInHelpText = this.portalSettings.LoginPageHelpingText.ConfigValue;
  readonly smsPasswordReset = this.portalSettings.SendSMSOnResetIfNoEmail.ConfigValue != 'E';
  readonly setPasswordInEnrollment = this.portalSettings.PortalEnrollmentMode.ConfigValue == 'D';
  readonly securityPinText = this.portalSettings.SecurityPinMessage.ConfigValue ?? 'Enter your security PIN:';
  readonly isWindowsPinRequired = this.portalSettings.WindowsLoginPinReqd.ConfigValue === '1';
  activeSubMenu: SubMenuType = SubMenuType.Default;
  activeMainMenu: MainMenuType = MainMenuType.Default;
  chosenCompany: number;
  isMobileMode = this.utilityService.isMobileMode();
  capsLockOn = false;
  isLoading = false;
  enableOnboarding = false;
  showFieldMode = false;
  returnUrl: string = null;
  companyConfig: LoginCompanyInfo = {
    isCoDropdownVisible: false,
    isCompanySelection: false,
    coDropdownCompanies: [],
    logoUrl: '',
    changeLogo: false,
    defaultCompany: null,
  };

  // login option flags:
  isViewpointLoginEnabled = true;
  isEmployeeLoginEnabled = true;
  isWindowsLoginEnabled = true;
  employeeLoginForm = new FormGroup({
    companySelect: new FormControl(null),
    employeeNum: new FormControl(null, Validators.required),
    employeePassword: new FormControl(null, Validators.required),
  });
  windowsLoginForm = new FormGroup({
    windowsUsername: new FormControl(null, Validators.required),
    windowsPassword: new FormControl(null, Validators.required),
    windowsPin: new FormControl(null),
  });
  enrollForm = new FormGroup({
    companySelect: new FormControl(null),
    employeeNum: new FormControl(null, Validators.required),
    securityPin: new FormControl(null, Validators.required),
    password: new FormControl(null),
    confirmPassword: new FormControl(null),
  });
  passwordResetForm = new FormGroup({
    companySelect: new FormControl(null),
    employeeNum: new FormControl(null, Validators.required),
    securityPin: new FormControl(null, Validators.required),
  });
  @ViewChild('employeeNum') employeeLoginNum: ElementRef;
  @ViewChild('windowsUsername') windowsUsername: ElementRef;
  isSMLogin = false;

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle(this.portalSettings.HRPortalName.ConfigValue);
    await this.getUrlParams();

    this.document.body.style.backgroundImage = "url('/Cache/images/trimble-background.png')";
    this.window.addEventListener('resize', event => this.windowResizeEvent(event));

    this.setLoginOptions();
    this.configureCompanySelect();

    if (this.enableViewpointOneLogin) {
      this.loginErrorMsg += ` If you have previously linked your employee login to ${this.isVistaTidEnabled ? 'Trimble' : 'Viewpoint'} ID, please log in that way.`;
    }
    if (!this.hideEnrollHelp) {
      this.loginErrorMsg += ' If you have forgotten your password, reset your password below.';
    }

    this.updateValidatorsFromPortalSettings();
  }

  ngOnDestroy(): void {
    this.window.removeEventListener('resize', event => this.windowResizeEvent(event));
  }

  async getUrlParams(): Promise<void> {
    this.isSMLogin = false;
    if (this.window.location.pathname.indexOf('/smlogin') > -1) {
      this.isSMLogin = true;
    }
    const query = this.window.location.search;
    const urlParams = new URLSearchParams(query);

    let url = urlParams.get('ReturnUrl');
    if (this.isSMLogin && (url == null || url == '')) {
      url = '/sm/manage';
    }
    const returnUrlResponse = await this.accountService.checkReturnUrl(url);
    this.returnUrl = returnUrlResponse['returnUrl'];

    const urlCoString = urlParams.get('co');
    if (urlCoString != null && urlCoString != '') {
      const companyNumResponse = await this.accountService.getCompanyNumFromUrl(urlCoString);
      this.companyFromUrl = parseInt(companyNumResponse['co']);
    }

    const link = urlParams.get('link');
    if (link === 'True') {
      this.openLinkUser();
    }

    const timedOut = urlParams.get('timedOut');
    if (timedOut == 'True') {
      this.notificationsService.showError('You have been logged out due to inactivity.', this.window.location.pathname);
    }

    const tidLoginFailure = urlParams.get('tidLoginFailure');
    if (tidLoginFailure == 'True') {
      this.notificationsService.showError(
        'There was an error with the TID login. Please contact your administrator.',
        this.window.location.pathname
      );
    }
  }

  configureCompanySelect(): void {
    if (this.companyFromUrl) {
      this.configureCompanyForCompanyFromUrl();
    } else {
      this.loginFormService.getCompanyConfiguration().subscribe(config => {
        this.companyConfig = config;
        this.chosenCompany = this.companyConfig.defaultCompany;
        this.setCompanySelect(this.companyConfig.defaultCompany);
        this.setCompanyValidationOnForms(this.companyConfig.isCompanySelection);
        this.setOnboardButtonVisibility(this.companyConfig.coDropdownCompanies);
      });
    }
  }

  configureCompanyForCompanyFromUrl(): void {
    const logoCo = this.portalSettings.LogoHQCo.ConfigValue;
    if (logoCo) {
      this.companyConfig.logoUrl = this.loginFormService.getLogoUrl(Number(logoCo));
    } else {
      this.companyConfig.logoUrl = this.loginFormService.getLogoUrl(this.companyFromUrl);
    }
    this.companyConfig.isCompanySelection = false;
    this.companyConfig.isCoDropdownVisible = false;
    this.setOnboardButtonVisibility([{ PRCo: this.companyFromUrl, PRCoNameMask: null }]);
    this.chosenCompany = this.companyFromUrl;
  }

  async setOnboardButtonVisibility(availableCompanies: any[]): Promise<void> {
    this.enableOnboarding = this.portalSettings.EnableOnboarding.ConfigValue === '1';

    if (this.enableOnboarding && availableCompanies !== null) {
      const onboardCompanies = this.getOnboardingCompanies();
      if (onboardCompanies && onboardCompanies.length > 0) {
        await onboardCompanies.forEach((co: any) => {
          const cur = availableCompanies.find((c: any) => c.PRCo == parseInt(co));
          if (cur) {
            this.companiesEnabledForOnboarding.push(cur);
          }
        });
        // no companies when comparing available and onboard companies, disable onboarding
        if (!this.companiesEnabledForOnboarding || this.companiesEnabledForOnboarding.length === 0) {
          this.enableOnboarding = false;
        }
      }
      // since there is no companies in the onboarding list, disable onboarding
      else {
        this.enableOnboarding = false;
      }
    } else {
      this.companiesEnabledForOnboarding = null;
      this.enableOnboarding = false;
    }
  }

  setLoginOptions(): void {
    if (this.isEMS_Active) {
      this.isWindowsLoginEnabled = false;
      this.isEmployeeLoginEnabled = false;
      this.isViewpointLoginEnabled = true;
    } else {
      switch (Number(this.portalSettings.SecurityMode.ConfigValue)) {
        case 1:
          this.isWindowsLoginEnabled = false;
          this.isEmployeeLoginEnabled = true;
          break;
        case 2:
          this.isWindowsLoginEnabled = true;
          this.isEmployeeLoginEnabled = false;
          break;
        case 5:
          this.isWindowsLoginEnabled = false;
          this.isEmployeeLoginEnabled = false;
          break;
        default:
          this.isWindowsLoginEnabled = true;
          this.isEmployeeLoginEnabled = true;
          break;
      }

      this.isViewpointLoginEnabled = this.portalSettings.ViewpointOneLoginEnabled.ConfigValue === '1';
    }
  }

  openLinkUser(): void {
    this.activeMainMenu = MainMenuType.Link;
  }

  changeMainMenu(menu: MainMenuType): void {
    if (this.companyConfig?.isCompanySelection && this.companyConfig?.isCoDropdownVisible) {
      this.updateCompanyOnMenuSwitch();
    }

    this.syncEmployeeNum(menu);
    if (this.activeMainMenu == MainMenuType.Enroll) {
      this.enrollForm.reset();
    }

    this.activeMainMenu = menu;

    if (this.activeMainMenu == MainMenuType.Default) {
      this.updateInputFocusForSubMenu();
    }
  }

  // Help keep the company dropdown in sync with the company selected in the other forms
  updateCompanyOnMenuSwitch(): void {
    switch (this.activeMainMenu) {
      case MainMenuType.Default:
        this.chosenCompany = this.employeeLoginForm.value.companySelect;
        break;
      case MainMenuType.Enroll:
        this.chosenCompany = this.enrollForm.value.companySelect;
        break;
      case MainMenuType.SignInHelp:
        this.chosenCompany = this.passwordResetForm.value.companySelect;
        break;
    }

    this.setCompanySelect(this.chosenCompany);
  }

  changeSubMenu(menu: SubMenuType): void {
    this.activeSubMenu = menu;
    this.updateInputFocusForSubMenu();
  }

  viewpointLogin(pmMode: boolean): void {
    const params: string[] = [];

    params.push(`pmMode=${pmMode}`);

    // Include relayUrl if returnUrl is valid
    if (this.returnUrl && this.returnUrl !== '') {
      const relayUrl = encodeURIComponent(this.returnUrl + window.location.hash);
      params.push(`relayUrl=${relayUrl}`);
    }
    const viewpointLoginUrl = '/Account/IdentityLogin?' + params.join('&');
    window.location.href = viewpointLoginUrl;
  }

  employeeLogin(loginType: LoginType = LoginType.Default): void {
    if (this.employeeLoginForm.invalid) {
      this.notificationsService.showError('Please enter your login credentials to sign in', this.window.location.pathname);
      return;
    }

    // Determine where we're getting the company number from
    let company = null;
    if (this.companyConfig?.isCompanySelection) {
      company = this.employeeLoginForm.value.companySelect;
    } else {
      company = this.chosenCompany;
    }
    this.isLoading = true;
    const loginData = {
      Employee: this.employeeLoginForm.value.employeeNum,
      Password: this.employeeLoginForm.value.employeePassword,
      PRCo: company,
      PMMode: loginType == LoginType.FieldMode && this.enableFieldMode,
    };

    this.accountService.login(loginData).then(
      (response: any) => {
        if (response.success) {
          if (this.isSMLogin) {
            this.window.location.href = '/sm/manage';
          } else {
            if (loginType == LoginType.ClockInOut && this.enableClockInOut) {
              this.window.location.href = '/Employee/ClockInOut';
            } else {
              if (this.returnUrl) {
                this.window.location.href = this.returnUrl + this.window.location.hash;
              } else {
                this.window.location.href = '/';
              }
            }
          }
        } else {
          this.isLoading = false;
          this.notificationsService.showError(this.loginErrorMsg, this.window.location.pathname);
        }
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  windowsLogin(loginType: LoginType = LoginType.Default): void {
    if (this.windowsLoginForm.invalid) {
      this.notificationsService.showError('Please enter your login credentials to sign in', this.window.location.pathname);
      return;
    }

    this.isLoading = true;

    const loginData = {
      username: this.windowsLoginForm.value.windowsUsername,
      password: this.windowsLoginForm.value.windowsPassword,
      loginPIN: this.windowsLoginForm.value.windowsPin,
      pmMode: loginType == LoginType.FieldMode && this.enableFieldMode,
      usingADEmail: this.portalSettings.LoginUsingADEmailField.ConfigValue === '1',
    };

    this.accountService.windowsLogin(loginData).then(
      (response: any) => {
        if (response.success) {
          if (loginType == LoginType.ClockInOut && this.enableClockInOut) {
            this.window.location.href = '/Employee/ClockInOut';
          } else if (this.returnUrl) {
            this.window.location.href = this.returnUrl + this.window.location.hash;
          } else {
            this.window.location.href = '/';
          }
        } else {
          this.isLoading = false;
          this.notificationsService.showError(response.errorMessage, this.window.location.pathname);
        }
      },
      () => {
        this.isLoading = false;
        this.notificationsService.showError('An error occurred while logging in. Please try again.', this.window.location.pathname);
      }
    );
  }

  goToOnboarding(): void {
    // If only one company is enabled for onboarding, go directly to that company
    if (this.companiesEnabledForOnboarding.length == 1) {
      this.window.location.href = '/Careers/Welcome#/C/' + this.companiesEnabledForOnboarding[0].PRCo;
    }
    // Otherwise choose from the list
    else if (this.companiesEnabledForOnboarding.length > 1) {
      // open company picker modal
      const modalRef = this.ngbModalService.open(LoginOnboardCoPickModalComponent, { size: 'md', backdrop: 'static' });
      modalRef.componentInstance.companies = this.companiesEnabledForOnboarding;
      modalRef.result
        .then((result: any) => {
          if (result) {
            this.window.location.href = '/Careers/Welcome#/C/' + result;
          }
        })
        .catch(() => {});
    }
  }

  enroll(): void {
    if (
      this.setPasswordInEnrollment &&
      this.doesPasswordsMatch(this.enrollForm.value.password, this.enrollForm.value.confirmPassword) == false
    ) {
      return;
    }

    this.isLoading = true;

    // Determine where we're getting the company number from
    let company = null;
    if (this.companyConfig?.isCompanySelection) {
      company = this.enrollForm.value.companySelect;
    } else {
      company = this.chosenCompany;
    }

    const accountData = {
      PRCo: company,
      Employee: this.enrollForm.value.employeeNum,
      Password: this.enrollForm.value.password,
      ConfirmPassword: this.enrollForm.value.confirmPassword,
      LastFourSSN: this.enrollForm.value.securityPin,
    };

    this.accountService.createAccount(accountData).then(
      (response: any) => {
        if (response.success) {
          if (response.msg == null) {
            if (this.returnUrl) this.window.location.href = this.returnUrl + this.window.location.hash;
            else this.window.location.href = '/';
          } else {
            this.notificationsService.showSuccess(response.msg);
            this.isLoading = false;
          }
        } else {
          // if not successful, bind errors to error variables
          this.isLoading = false;
          if (response.errorMessage == null) {
            this.notificationsService.showError(response.msg, this.window.location.pathname);
            if (response.url != null) {
              this.window.location.href = response.url;
            }
          } else {
            this.notificationsService.showError(response.errorMessage, this.window.location.pathname);
          }
        }
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  resetPassword(): void {
    this.isLoading = true;

    // Determine where we're getting the company number from
    let company = null;
    if (this.companyConfig?.isCompanySelection) {
      company = this.passwordResetForm.value.companySelect;
    } else {
      company = this.chosenCompany;
    }

    this.accountService
      .requestPasswordReset(this.passwordResetForm.value.employeeNum, company, this.passwordResetForm.value.securityPin)
      .then(
        (response: any) => {
          if (response.success && response.isSent) {
            if (response.isSMSRequest) {
              this.router.navigate(['/account/resetaccountpassword'], { queryParams: { manualToken: true } });
            } else {
              this.isLoading = false;
              this.notificationsService.showSuccess(response.responseMsg);
              this.changeMainMenu(MainMenuType.Default);

              // clearing all but the company number dropdowns
              this.employeeLoginForm.controls['employeeNum'].setValue('');
              this.employeeLoginForm.controls['employeePassword'].setValue('');
              this.windowsLoginForm.controls['windowsUsername'].setValue('');
              this.windowsLoginForm.controls['windowsPassword'].setValue('');
              this.enrollForm.controls['employeeNum'].setValue('');
              this.enrollForm.controls['securityPin'].setValue('');
              this.enrollForm.controls['password'].setValue('');
              this.enrollForm.controls['confirmPassword'].setValue('');
              this.passwordResetForm.controls['employeeNum'].setValue('');
              this.passwordResetForm.controls['securityPin'].setValue('');
            }
          } else {
            this.isLoading = false;
            this.notificationsService.showError(response.responseMsg, this.window.location.pathname);
          }
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  getOnboardingCompanies(): any {
    const enableOnboardLoginByCompany = this.portalSettings.EnableOnboardLoginByCompany.ConfigValue;
    if (enableOnboardLoginByCompany !== null && enableOnboardLoginByCompany !== '' && enableOnboardLoginByCompany !== undefined) {
      return enableOnboardLoginByCompany.split(',');
    } else {
      return null;
    }
  }

  setCompanySelect(co: number): void {
    this.employeeLoginForm.controls['companySelect'].setValue(co);
    this.enrollForm.controls['companySelect'].setValue(co);
    this.passwordResetForm.controls['companySelect'].setValue(co);
  }

  setCompanyValidationOnForms(isCompanySelection: boolean): void {
    if (isCompanySelection) {
      this.employeeLoginForm.get('companySelect').setValidators(Validators.required);
      this.enrollForm.get('companySelect').setValidators(Validators.required);
      this.passwordResetForm.get('companySelect').setValidators(Validators.required);
    } else {
      this.employeeLoginForm.get('companySelect').clearValidators();
      this.enrollForm.get('companySelect').clearValidators();
      this.passwordResetForm.get('companySelect').clearValidators();
    }

    this.employeeLoginForm.updateValueAndValidity();
    this.enrollForm.updateValueAndValidity();
    this.passwordResetForm.updateValueAndValidity();
  }

  updateCompanyLogo(event: Event): void {
    const selectCoElement = event.target as HTMLSelectElement;
    if (this.companyConfig?.changeLogo && this.companyConfig?.isCoDropdownVisible) {
      this.companyConfig.logoUrl = this.loginFormService.getLogoUrl(Number(selectCoElement.value));
    }
  }

  goToPasswordReset(): void {
    this.router.navigate(['/account/resetaccountpassword'], { queryParams: { manualToken: true } });
  }

  windowResizeEvent(event): void {
    if (this.window.innerWidth < 768 || this.utilityService.isMobileMode()) this.isMobileMode = true;
    else this.isMobileMode = false;
  }

  doesPasswordsMatch(password1: string, password2: string): boolean {
    if (password1 != password2) {
      this.notificationsService.showError('Passwords must match!', this.window.location.pathname);
      return false;
    } else {
      return true;
    }
  }

  syncEmployeeNum(menu: MainMenuType): void {
    // Sync employee number between sign in help and employee login forms
    if (this.activeMainMenu == MainMenuType.SignInHelp) {
      this.employeeLoginForm.controls['employeeNum'].setValue(this.passwordResetForm.value.employeeNum);
    } else if (menu == MainMenuType.SignInHelp) {
      this.passwordResetForm.controls['employeeNum'].setValue(this.employeeLoginForm.value.employeeNum);
    }
  }

  updateValidatorsFromPortalSettings(): void {
    // update windows login page to include the pin when it's required.
    if (this.isWindowsPinRequired) {
      this.windowsLoginForm.get('windowsPin').setValidators(Validators.required);
    } else {
      this.windowsLoginForm.get('windowsPin').clearValidators();
    }
    this.windowsLoginForm.get('windowsPin').updateValueAndValidity();

    // update enroll form to include password requirements
    if (this.setPasswordInEnrollment) {
      this.enrollForm.get('password').setValidators(Validators.required);
      this.enrollForm.get('confirmPassword').setValidators(Validators.required);
    } else {
      this.enrollForm.get('password').clearValidators();
      this.enrollForm.get('confirmPassword').clearValidators();
    }
    this.enrollForm.get('password').updateValueAndValidity();
    this.enrollForm.get('confirmPassword').updateValueAndValidity();
  }

  updateInputFocusForSubMenu(): void {
    setTimeout(() => {
      if (this.activeSubMenu == SubMenuType.EmployeeLogin) {
        this.employeeLoginNum?.nativeElement?.focus();
      } else if (this.activeSubMenu == SubMenuType.WindowsLogin) {
        this.windowsUsername?.nativeElement?.focus();
      }
    }, 100);
  }

  clearForms(): void {
    this.employeeLoginForm.reset();
    this.windowsLoginForm.reset();
    this.enrollForm.reset();
    this.passwordResetForm.reset();
  }

  toggleFieldMode(): void {
    this.showFieldMode = !this.showFieldMode;
  }
}
